/* eslint-disable no-underscore-dangle */
import React from 'react';

import {
  Button,
  Form,
  RadioButton,
  RadioGroup,
  Select,
  SelectOption,
  Text,
} from 'components';

import {
  OrgLocalOnboardingProfile,
  PortalWithAssociationDataConfig,
  PrimaryRole as PrimaryRoleEnum,
} from '../../../types';

export type PrimaryRoleFormProps = {
  overrideLabel?: PortalWithAssociationDataConfig['overrideLabel'];
  onSubmit: (values: PrimaryRoleFormValues) => void;
};

export type PrimaryRole = {
  label: string;
  value: PrimaryRoleEnum;
};

const DEFAULT_OPTIONS: PrimaryRole[] = [
  { label: 'Administrator', value: PrimaryRoleEnum.Administrator },
  { label: 'Office Manager', value: PrimaryRoleEnum.OfficeManager },
  { label: 'Brokerage Owner', value: PrimaryRoleEnum.BrokerageOwner },
];

const getOptions = (brokerOfRecordLabel?: string) => [
  ...DEFAULT_OPTIONS,
  {
    label: brokerOfRecordLabel || 'Broker of Record',
    value: PrimaryRoleEnum.BoR,
  },
];

export type PrimaryRoleFormValues = Pick<
  OrgLocalOnboardingProfile,
  'isLicensed' | 'primaryRole'
>;

const PrimaryRoleForm: React.FC<PrimaryRoleFormProps> = props => {
  const { overrideLabel, onSubmit } = props;

  const [primaryRole, setPrimaryRole] =
    React.useState<PrimaryRoleFormValues['primaryRole']>(null);
  const [isLicensed, setIsLicensed] =
    React.useState<PrimaryRoleFormValues['isLicensed']>(null);

  const isBrokerOfRecord = primaryRole === PrimaryRoleEnum.BoR;

  const _handleSubmit = () => {
    const values = {
      primaryRole,
      isLicensed: isBrokerOfRecord || isLicensed,
    };

    onSubmit(values);
  };

  return (
    <Form name="primaryRoleForm" layout="vertical" onFinish={_handleSubmit}>
      <Form.Item name="primaryRole" label="Primary Role">
        <Select
          placeholder="Select"
          value={primaryRole}
          onChange={role => setPrimaryRole(role)}
          data-cy="claimBrokerage-select-primaryRole"
        >
          {getOptions(overrideLabel).map(({ label, value }) => (
            <SelectOption key={value} value={value} label={label}>
              <Text>{label}</Text>
            </SelectOption>
          ))}
        </Select>
      </Form.Item>

      {!isBrokerOfRecord && (
        <Form.Item name="isLicensed" label="Do you have a member number?">
          <RadioGroup
            value={isLicensed}
            buttonStyle="solid"
            onChange={e => setIsLicensed(e.target.value)}
            dataCy="claimBrokerage-radioGroup-isLicensed"
          >
            <RadioButton name="isLicensed" value>
              Yes
            </RadioButton>
            <RadioButton name="isLicensed" value={false}>
              No
            </RadioButton>
          </RadioGroup>
        </Form.Item>
      )}

      <Button
        type="primary"
        block
        htmlType="submit"
        data-cy="next-button"
        disabled={
          primaryRole === null || (isLicensed === null && !isBrokerOfRecord)
        }
      >
        Next
      </Button>
    </Form>
  );
};

export default PrimaryRoleForm;
